/**
 * Centralized Toast Event Configuration
 * 
 * Define all toast events here. Each event has:
 * - type: toast variant (info, success, error, warning)
 * - message: function that generates the display message
 * - dedupKey: optional dedup function (prevents duplicate toasts within dedupTtl)
 * - dedupTtl: dedup window in ms (default 3000)
 * 
 * Usage:
 *   import { eventBus } from '@/lib/event-bus';
 *   eventBus.emit('price:updated', { drugId: 'x', price: 1000, name: 'Drug X' });
 */

export interface ToastEventConfig<T = any> {
  type: 'info' | 'success' | 'error' | 'warning';
  message: (data: T) => string;
  dedupKey?: (data: T) => string;
  dedupTtl?: number; // ms, default 3000
}

// ── Event type definitions ──

export interface PriceUpdatedEvent {
  drugId: string;
  price: number;
  name: string;
}

export interface PrescriptionNewEvent {
  prescriptionId: string;
  patientName?: string;
}

export interface SaveEvent {
  count: number;
  label?: string;
}

export interface ScanErrorEvent {
  message?: string;
}

// ── Toast registry ──

export const toastEvents: Record<string, ToastEventConfig> = {
  'price:updated': {
    type: 'info',
    message: (data: PriceUpdatedEvent) => `💰 Giá đã cập nhật: ${data.name || data.drugId}`,
    dedupKey: (data: PriceUpdatedEvent) => `price:${data.drugId}:${data.price}`,
    dedupTtl: 5000,
  },

  'prescription:new': {
    type: 'info',
    message: (data: PrescriptionNewEvent) => `🆕 Toa thuốc mới: ${data.patientName || data.prescriptionId}`,
    dedupKey: (data: PrescriptionNewEvent) => `rx:${data.prescriptionId}`,
    dedupTtl: 5000,
  },

  'save:success': {
    type: 'success',
    message: (data: SaveEvent) => data.count > 1 ? `Đã lưu ${data.count} thay đổi` : 'Đã lưu!',
  },

  'save:error': {
    type: 'error',
    message: (data: ScanErrorEvent) => data.message || 'Lỗi khi lưu!',
  },

  'scan:error': {
    type: 'error',
    message: (data: ScanErrorEvent) => data.message || 'Scan failed',
  },
};
